/**
 * THE TELEPORT — press it and you are somewhere else, instantly, and nobody can follow.
 *
 * The player report: "another power-up could be a teleport where when used, it teleports the
 * circle invoking it instantly to a random location on the map (used for when in serious trouble
 * and wants to get away from chasing circles)".
 *
 * **IT IS THE ABILITY THAT PUTS NOTHING IN THE WORLD, and that is what it is here to prove.** The
 * design notes on the abilities card named a shield as the case that would make `ProjectileSpec`
 * optional — "exactly the kind of pressure the registry should be put under before it is trusted"
 * — and this is that case arriving first. There is no body to fly, nothing to collide with,
 * nothing to arm and nothing to retire: firing it is over on the tick it is pressed. So it
 * carries a `SelfSpec` where the other five carry a `ProjectileSpec`, and `RoomSim` reads it in
 * the phase that sits beside `stepSplits` and `stepEjects` rather than in `stepProjectiles`.
 *
 * **IT IS THE FIRST ABILITY THAT DRAWS FROM THE ROOM'S SEEDED STREAM.** Every other one takes the
 * heading its firer was already steering on and therefore costs no slot in the seeded sequence —
 * the rule `splitCluster` and `ejectPellet` follow. A *random* destination cannot: it is a draw,
 * and it is a draw made because somebody pressed a button, which is exactly what the room's
 * shared `rng` is for. Deliberately **not** `powerUpRng`: that generator exists because orbs
 * appear when *time passes*, and a draw on it would make where a joining player lands a function
 * of how long the room had been idle. A teleport is an event, so it goes on the event stream,
 * next to a spawn, a shatter and a duel's timed-out answer.
 *
 * **WHERE IT MAY LAND IS THE SPAWN PLACEMENT, NOT A BARE UNIFORM DRAW.** A point is taken the way
 * a joining player's is — SPAWN_EDGE_MARGIN inside every wall, off the same helper — and then
 * *rejected* while a spike or somebody big enough to eat you is sitting on it. Without that, an
 * ability whose entire purpose is escaping a chase would be a button that occasionally drops you
 * into a hazard or into a bigger mouth, which is worse than not pressing it.
 *
 * **THE WHOLE CLUSTER GOES.** A split player who teleported one cell would be torn in half across
 * the map, holding two bodies that can never reach each other again — a punishment nobody asked
 * for out of a button pressed to survive. Every cell moves by the same offset, so the arrangement
 * a player is in arrives with them.
 */

import { TICK_RATE_HZ } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';

export const POWER_UP_TELEPORT = 'teleport';

/**
 * How long the arrival cannot be touched, in seconds — **half of one**, and it is deliberately
 * far shorter than a duel's.
 *
 * THERE IS A GRACE, AND THE ARGUMENT FOR IT IS THE ABILITY'S OWN PURPOSE. This is pressed by
 * somebody in serious trouble; landing on top of a stranger and being swallowed on the same tick
 * would make it worse than useless, and would make the one moment it is for the one moment it
 * cannot be trusted. It is `duelImmuneUntil`'s window — see DUEL_IMMUNITY_TICKS, which is the
 * precedent and is three times as long — reused rather than copied, because "cannot be eaten and
 * cannot be pulled into a duel" is exactly what is wanted and already exists.
 *
 * Short, though, and for two reasons. The destination was already chosen clear of anybody who
 * could eat this player, so what the window is covering is only somebody who was *moving* toward
 * that spot: ten ticks is enough for the arrival to be a fact and not enough to be a weapon.
 * And the grace runs both ways — an immune player eats nobody either — so a long one would make
 * a teleport an offensive opening rather than an escape, which is not what was asked for.
 */
export const TELEPORT_GRACE_SECONDS = 0.5;

/** The same number the simulation counts in. `PlayerState.duelImmuneUntil` is a tick. */
export const TELEPORT_GRACE_TICKS = TELEPORT_GRACE_SECONDS * TICK_RATE_HZ;

/**
 * How much empty space the arrival wants around it, in world units, on top of the cluster's own
 * extent.
 *
 * A little over three times SPIKE_RADIUS and about a third of the interest radius: far enough
 * that a player arrives with a moment to read the screen and steer, near enough that a room with
 * a crowd in it still has somewhere to put them. It is measured from the cluster's *extent*
 * rather than from its centroid (`clusterViewRadius`), so a player in eight pieces is given the
 * room those eight pieces actually occupy.
 */
export const TELEPORT_LANDING_CLEARANCE = 200;

/**
 * How many points are drawn before one is taken whatever is standing on it.
 *
 * SPIKE_PLACEMENT_ATTEMPTS' arrangement exactly, and for its reason: a rejection sampler with no
 * ceiling is a tick with no bound on it. Eight draws is a vanishing chance of no clear point in
 * any room this game runs — and if there genuinely is none, the grace above is what makes taking
 * the last draw anyway survivable.
 */
export const TELEPORT_PLACEMENT_ATTEMPTS = 8;

/**
 * Firings one orb is worth. **One.** The report named no model for the teleport either, and this
 * is the entry where the answer is the ability's own description: "used for when in serious
 * trouble and wants to get away from chasing circles."
 *
 * **The whole value of an escape is that you have it once, at the moment you need it.** A player
 * carrying two cannot be cornered at all — the first jump is free, so it gets spent on a scare,
 * and the second is still there for the real one. A timed window is worse again: it would be an
 * escape that expires while nobody is chasing you, which is precisely when nobody presses it.
 *
 * One charge is also exactly what this ability did before there were use models at all, so the
 * teleport is the entry that shows `charged` is a generalisation rather than a change: it is the
 * smallest budget there is, and it needs no special case anywhere.
 */
export const TELEPORT_CHARGES = 1;

/**
 * The gap between two jumps, in ticks — **TELEPORT_GRACE_TICKS, half a second.**
 *
 * Unreachable with TELEPORT_CHARGES at one, and declared anyway rather than left at zero: the
 * model requires a number, a zero would say "as fast as the wire allows" about an ability that
 * moves a whole cluster across the map, and this is the honest one — you may not jump again
 * before the grace the last jump granted has run out. It is what a second charge would cost, the
 * day a card wants one.
 */
export const TELEPORT_FIRE_COOLDOWN_TICKS = TELEPORT_GRACE_TICKS;

export const teleportPowerUp: PowerUp = {
  id: POWER_UP_TELEPORT,
  name: 'Teleport',
  description: 'Jumps your whole cluster to somewhere else on the map, clear of trouble.',
  glyph: '🌀',
  use: {
    model: 'charged',
    charges: TELEPORT_CHARGES,
    cooldownTicks: TELEPORT_FIRE_COOLDOWN_TICKS,
  },
  self: {
    effect: 'teleport',
    graceTicks: TELEPORT_GRACE_TICKS,
    landingClearance: TELEPORT_LANDING_CLEARANCE,
    placementAttempts: TELEPORT_PLACEMENT_ATTEMPTS,
  },
};
